/*
 * © 2023 Vertafore, Inc. All rights reserved.
 * Reproduction and distribution without the written permission of Vertafore is prohibited.
 */
import { InitialStateType } from "../../app/store/rootType";
import React, { Component, ReactElement } from "react";
import { Modal, Button } from "react-bootstrap";
import {
  addTenantController,
  getAllTenantInfoController,
  updateTenantController,
} from "../../app/store/provisioning/controller";
import { connect, ConnectedProps } from "react-redux";
import { TenantInfo } from "../../types/customTypes/ratModels";
import "./tenantList.css";

interface State {
  tenantIdText: string;
  tenantNameText: string;
  agencyIdText: number;
  partnerIdText: string;
  status: string;
  notes: string;
  errors: { [key: string]: any };
  loading: boolean;
}

interface Props {
  showModal: boolean;
  modalType: string;
  selectedTenant: TenantInfo | undefined;
  closeModal: () => void;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: InitialStateType) => ({});

const mapDispatchToProps = {
  addTenantController: addTenantController,
  updateTenantController: updateTenantController,
  getAllTenantInfoController,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type FinalProps = PropsFromRedux & Props;

class addTenantComponent extends Component<FinalProps, State> {
  constructor(props: FinalProps) {
    super(props);
    this.state = {
      tenantIdText: this.props.selectedTenant?.tenantId ?? "",
      tenantNameText: this.props.selectedTenant?.tenantName ?? "",
      agencyIdText: this.props.selectedTenant?.ratingAgencyId ?? 0,
      partnerIdText: this.props.selectedTenant?.partnerId ?? "",
      status: this.props.selectedTenant?.status ?? "",
      notes: this.props.selectedTenant?.notes ?? "",
      errors: {},
      loading: false,
    };
  }

  componentDidUpdate = async (
    prevProps: Readonly<FinalProps>
  ): Promise<void> => {
    if (this.props.selectedTenant !== prevProps.selectedTenant) {
      this.setState({
        tenantIdText: this.props.selectedTenant?.tenantId ?? "",
        tenantNameText: this.props.selectedTenant?.tenantName ?? "",
        agencyIdText: this.props.selectedTenant?.ratingAgencyId ?? 0,
        partnerIdText: this.props.selectedTenant?.partnerId ?? "",
        status: this.props.selectedTenant?.status ?? "",
        notes: this.props.selectedTenant?.notes ?? "",
      });
    }
  };

  tenantId = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({
      tenantIdText: e.target.value,
    });
  };

  tenantName = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({
      tenantNameText: e.target.value,
    });
  };

  agencyId = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({
      agencyIdText: parseInt(e.target.value),
    });
  };

  partnerId = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({
      partnerIdText: e.target.value,
    });
  };

  status = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    this.setState({
      status: e.target.value,
    });
  };

  notes = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.setState({
      notes: e.target.value,
    });
  };

  closeTenantModal = (): void => {
    if (this.props.selectedTenant === undefined) {
      this.setState({
        errors: {},
        tenantIdText: "",
        tenantNameText: "",
        agencyIdText: 0,
        partnerIdText: "",
        status: "",
        notes: "",
      });
    }
    this.props.closeModal();
  };

  isValid = (): boolean => {
    const tenantErrors: { [k: string]: any } = {};
    if (
      this.state.tenantIdText.length < 1 &&
      this.props.selectedTenant === undefined
    ) {
      tenantErrors.tenantIdErr = "TenantId can't be empty!";
    }
    if (
      this.state.tenantNameText.length < 1 &&
      this.props.selectedTenant === undefined
    ) {
      tenantErrors.tenantNameErr = "Tenant Name can't be empty!";
    }
    if (
      this.state.agencyIdText === 0 &&
      this.props.selectedTenant === undefined
    ) {
      tenantErrors.agencyIdErr = "AgencyId can't be empty!";
    }
    if (this.state.status === "" && this.props.selectedTenant === undefined) {
      tenantErrors.agencyIdErr = "Status can't be empty!";
    }
    if (
      (tenantErrors.tenantIdErr !== undefined &&
        tenantErrors.tenantIdErr !== "") ||
      (tenantErrors.tenantNameErr !== undefined &&
        tenantErrors.tenantNameErr !== "") ||
      (tenantErrors.agencyIdErr !== undefined &&
        tenantErrors.agencyIdErr !== "")
    ) {
      this.setState({ errors: tenantErrors });
      return false;
    }
    return true;
  };

  addTenant = async (): Promise<void> => {
    if (this.isValid()) {
      this.setState({
        loading: true,
      });
      if (this.props.selectedTenant == null) {
        await this.props.addTenantController(
          this.state.tenantIdText,
          this.state.tenantNameText,
          this.state.agencyIdText,
          this.state.partnerIdText,
          this.state.status,
          this.state.notes
        );
      } else {
        await this.props.updateTenantController(
          this.state.tenantIdText,
          this.state.tenantNameText,
          this.state.agencyIdText,
          this.state.partnerIdText,
          this.state.status,
          this.state.notes
        );
      }

      this.setState({
        loading: false,
      });
      this.closeTenantModal();
    }
  };

  loadAllTenantInfo = async (): Promise<void> => {
    this.setState({
      loading: true,
    });
    await this.props.getAllTenantInfoController();

    this.setState({
      loading: false,
    });
  };

  render = (): ReactElement => {
    return (
      <Modal size="sm" show={this.props.showModal} className="mt-5">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header d-block text-center">
              <h5 className="modal-title">{this.props.modalType} Tenant</h5>
            </div>
            <div className="modal-body">
              <div className="form-group">
                {this.props.modalType === "Update" ? (
                  <div>
                    <label className="select-label ml-3 text-orng">
                      Tenant Id:{" " + this.state.tenantIdText}
                    </label>
                  </div>
                ) : (
                  <div>
                    <label
                      className="select-label ml-3 text-orng"
                      htmlFor="tenantId"
                    >
                      Tenant Id:{" "}
                    </label>
                    <input
                      type="text"
                      name="tenantId"
                      className="form-control"
                      placeholder="Tenant Id"
                      value={this.state.tenantIdText}
                      onChange={this.tenantId}
                    />
                  </div>
                )}
                <span className="text-danger">
                  {this.state.errors.tenantIdErr}
                </span>
              </div>
              <div className="form-group">
                <div>
                  <label className="select-label ml-3 text-orng">
                    Tenant Name:{" "}
                  </label>
                  <input
                    type="text"
                    name="tenantName"
                    placeholder="Tenant Name"
                    value={this.state.tenantNameText}
                    className="form-control"
                    onChange={this.tenantName}
                  />
                </div>
                <span className="text-danger">
                  {this.state.errors.tenantNameErr}
                </span>
              </div>
              <div className="form-group">
                {this.props.modalType === "Update" ? (
                  <div>
                    <label className="select-label ml-3 text-orng">
                      Rating Agency Id:
                      {" " + this.state.agencyIdText.toString()}
                    </label>
                  </div>
                ) : (
                  <div>
                    <label className="select-label ml-3 text-orng">
                      Rating Agency Id:{" "}
                    </label>
                    <input
                      type="text"
                      name=""
                      placeholder="Rating Agency Id"
                      value={this.state.agencyIdText}
                      className="form-control"
                      onChange={this.agencyId}
                    />
                  </div>
                )}
                <span className="text-danger">
                  {this.state.errors.agencyIdErr}
                </span>
              </div>
              <div className="form-group">
                <div>
                  <label className="select-label ml-3 text-orng">
                    Partner Id:{" "}
                  </label>
                  <input
                    type="number"
                    name=""
                    value={this.state.partnerIdText}
                    placeholder="Partner Id"
                    className="form-control"
                    onChange={this.partnerId}
                  />
                </div>
              </div>
              <div className="form-group">
                <div>
                  <label className="select-label ml-3 text-orng">Notes: </label>
                  <input
                    type="text"
                    name=""
                    value={this.state.notes}
                    className="form-control"
                    onChange={this.notes}
                  />
                </div>
              </div>
              <div className="form-group">
                <div>
                  <label className="select-label ml-3 text-orng">
                    Status:{" "}
                  </label>
                  <select
                    name="status"
                    onChange={(event) => this.status(event)}
                    className="customSelect"
                    value={this.state.status}
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="CANCEL">CANCEL</option>
                  </select>
                </div>
              </div>
              <div className="text-xs-center">
                <div>
                  <Button
                    variant="secondary"
                    onClick={this.closeTenantModal}
                    className="float-left"
                  >
                    Cancel
                  </Button>
                  {this.state.loading ? (
                    <Button
                      className="v-btn float-right"
                      type="button"
                      disabled
                    >
                      <span
                        className="spinner-border spinner-border-sm mr-1"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Saving...
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={this.addTenant}
                      className="v-btn float-right"
                    >
                      Save
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  };
}

export default connector(addTenantComponent);
